import React, { useState } from "react";
import { useCart } from "../contexts/CartContext";
import { List, message, Divider, Button, Card, Typography, Grid } from "antd";
import { ShoppingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import CartActions from "../components/cart/CartActions";
import CartItem from "../components/cart/CartItem";
import CartSummary from "../components/cart/CartSummary";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const Cart = () => {
  const { carts, removeFromCart, updateQuantity, deleteCart } = useCart();
  const [checkedItems, setCheckedItems] = useState({});
  const screens = useBreakpoint();

  const handleRemove = async (id, product) => {
    try {
      await removeFromCart(id, product);
      message.success("Product removed from cart!");
    } catch (error) {
      message.error("Failed to remove item from cart. Please try again");
      console.log("Failed to remove item from cart", error);
    }
  };

  const handleDeleteCart = async (id) => {
    try {
      await deleteCart(id);
      message.success("Cart deleted!");
    } catch (error) {
      message.error("Failed to delete cart. Please try again");
      console.error("Failed to delete cart", error);
    }
  };

  const handleQuantityChange = async (id, product, quantity) => {
    try {
      await updateQuantity(id, product, quantity);
      setCheckedItems((prevCheckedItems) => {
        const newCheckedItems = { ...prevCheckedItems };
        if (newCheckedItems[`${id}-${product}`]) {
          newCheckedItems[`${id}-${product}`] = true;
        }
        return newCheckedItems;
      });
    } catch (error) {
      message.error("Failed to update quantity. Please try again");
      console.error("Failed to update quantity.", error.message);
    }
  };

  const handleCheckboxChange = (cartId, productId, checked) => {
    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [`${cartId}-${productId}`]: checked,
    }));
  };

  const handleCheckAllChange = (cartId, checked) => {
    const newCheckedItems = { ...checkedItems };
    const cart = carts.find((c) => c._id === cartId);
    if (cart) {
      cart.items.forEach((item) => {
        newCheckedItems[`${cartId}-${item.product._id}`] = checked;
      });
    }
    setCheckedItems(newCheckedItems);
  };

  const isChecked = (cartId, productId) => {
    return checkedItems[`${cartId}-${productId}`] || false;
  };

  const getTotalCheckedItems = (cartId) => {
    let totalItems = 0;
    const cart = carts.find((c) => c._id === cartId);
    if (cart) {
      cart.items.forEach((item) => {
        if (checkedItems[`${cartId}-${item.product._id}`]) {
          totalItems += item.quantity;
        }
      });
    }
    return totalItems;
  };

  const getTotalCheckedPrice = (cartId) => {
    let totalPrice = 0;
    const cart = carts.find((c) => c._id === cartId);
    if (cart) {
      cart.items.forEach((item) => {
        if (checkedItems[`${cartId}-${item.product._id}`]) {
          totalPrice += item.product.price * item.quantity;
        }
      });
    }
    return totalPrice.toFixed(2);
  };

  const handleBuyNow = (cartId) => {
    const cart = carts.find((c) => c._id === cartId);
    if (!cart) return { cartId, items: [], allItemsChecked: false };
    const items = cart.items.filter(
      (item) => checkedItems[`${cartId}-${item.product._id}`],
    );
    const allItemsChecked = items.length === cart.items.length;
    return { cartId, items, allItemsChecked };
  };

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        padding: screens.xs ? "12px 8px" : "24px 16px",
      }}
    >
      {carts.length > 0 ? (
        carts.map((cart) => (
          <Card
            key={cart._id}
            style={{
              marginBottom: "20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: screens.xs ? "12px" : "20px" }}
          >
            <CartActions
              cart={cart}
              handleDeleteCart={handleDeleteCart}
              handleCheckAllChange={handleCheckAllChange}
            />

            <List
              itemLayout="horizontal"
              dataSource={cart.items}
              renderItem={(item) => (
                <CartItem
                  cartId={cart._id}
                  item={item}
                  isChecked={isChecked}
                  handleCheckboxChange={handleCheckboxChange}
                  handleQuantityChange={handleQuantityChange}
                  handleRemove={handleRemove}
                  cart={cart}
                />
              )}
            />

            <Divider style={{ margin: "16px 0" }} />

            <CartSummary
              totalItems={getTotalCheckedItems(cart._id)}
              totalPrice={getTotalCheckedPrice(cart._id)}
              handleBuyNow={handleBuyNow(cart._id)}
            />
          </Card>
        ))
      ) : (
        <div
          style={{
            display: "flex",
            minHeight: "50vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <Title level={3}>Your cart is empty</Title>
          <Button type="primary" size="large" icon={<ShoppingOutlined />}>
            <Link to="/buy/product">Continue Shopping</Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Cart;
